import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { mergeTpl } from "@/lib/printTemplates";
import { useHistory, useUndoKeys } from "@/lib/useHistory";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Save, Undo2, Redo2, X } from "lucide-react";

export function useConfigEditor(key, defaults) {
  const h = useHistory(null);
  const [saved, setSaved] = useState(null);
  useUndoKeys(h);
  const { reset } = h;
  useEffect(() => {
    api.get(`/config/${key}`).then(({ data }) => {
      const v = defaults ? mergeTpl(defaults, data) : data;
      setSaved(v); reset(v);
    }).catch((e) => toast.error(apiError(e)));
  }, [key, defaults, reset]);
  const save = async () => {
    try { await api.put(`/config/${key}`, h.value); setSaved(h.value); toast.success("Запазено"); } catch (e) { toast.error(apiError(e)); }
  };
  return { ...h, dirty: saved !== h.value && JSON.stringify(saved) !== JSON.stringify(h.value), save, cancel: () => reset(saved) };
}

export const EditorBar = ({ ed, children, testid }) => (
  <div className="flex flex-wrap items-center gap-2 mb-4">
    {children}
    <div className="ml-auto flex gap-2">
      <Button variant="outline" size="icon" disabled={!ed.canUndo} onClick={ed.undo} title="Отмени (Ctrl+Z)" data-testid={`${testid}-undo`}><Undo2 className="w-4 h-4" /></Button>
      <Button variant="outline" size="icon" disabled={!ed.canRedo} onClick={ed.redo} title="Върни (Ctrl+Y)" data-testid={`${testid}-redo`}><Redo2 className="w-4 h-4" /></Button>
      <Button variant="outline" disabled={!ed.dirty} onClick={ed.cancel} className="gap-1" data-testid={`${testid}-cancel`}><X className="w-4 h-4" /> Отказ</Button>
      <Button disabled={!ed.dirty} onClick={ed.save} className="bg-forest text-white gap-1" data-testid={`${testid}-save`}><Save className="w-4 h-4" /> Запази</Button>
    </div>
  </div>
);

export const Field = ({ label, value, onChange, type = "text", testid, ...rest }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <Input type={type} value={value ?? ""} onChange={(e) => onChange(type === "number" ? Number(e.target.value) : e.target.value)} data-testid={testid} {...rest} />
  </div>
);

export const Toggle = ({ label, checked, onChange, disabled, testid }) => (
  <label className={`flex items-center justify-between gap-2 py-1 text-sm ${disabled ? "opacity-60" : ""}`}>
    <span>{label}</span><Switch checked={!!checked} onCheckedChange={onChange} disabled={disabled} data-testid={testid} />
  </label>
);

export const Panel = ({ title, children }) => (
  <div className="bg-white rounded-2xl border border-gold/10 shadow-soft p-4 space-y-3">
    {title && <div className="font-semibold text-forest-deep">{title}</div>}
    {children}
  </div>
);
